import React, {
    useEffect,
    useContext,
    useCallback,
    createContext,
    useReducer,
    useRef
} from 'react';
import { navigate } from 'gatsby';

import { findIndex, forEach, isEmpty, keys, map, find } from 'lodash';

import CartReducer from '~/reducers/cart';

import { useLocationPickerContext } from '~/context/LocationPickerContext';
import { useUser } from '~/context/UserContext';

import { getTotals, updateRecipientsIfLoggedIn } from '~/utilities/cart';

import {
    addItemToCart,
    removeItemFromCart,
    updateCartItemAddOns,
    updateAddOnPaymentMethod,
    removeCartItemAddOn,
    updateCartPromoCode,
    removePromoCode,
    updateCartItemQuantity,
    updateCartItemLocation,
    updateCartItemPrice,
    syncCartToStorage
} from '~/utilities/cartActions';

import { normalizeCartItemsForDisplay } from '~/utilities/cartNormalize';

import cartHeartbeat from '~/utilities/cartHeartbeat';

const CART_STORAGE_KEY = 'ab_cart';

const CartContext = createContext();

const initialCartState = {
    cart_id: '',
    items: [],
    itemsUpdated: false,
    newItem: {
        title: '',
        addOns: []
    },
    loading: true,
    totals: {
        tax: 0,
        shipping: 0,
        subTotal: 0,
        grandTotal: 0,
        discount: 0,
        giftCardBalance: 0
    },
    discounts: [],
    shippingInfo: {},
    customerInfo: {},
    hasMounted: false,
    cartError: null
};

export const CartContextProvider = ({ children }) => {
    const [state, dispatch] = useReducer(CartReducer, initialCartState);
    const { user, isLoggedIn, loading: userLoading } = useUser();
    const { locations, selectedLocation } = useLocationPickerContext();
    const prevIsLoggedIn = useRef(isLoggedIn);
    const prevSelectedLocation = useRef(null);
    const stateRef = useRef(state);
    stateRef.current = state;

    const handleCartError = useCallback((error) => {
        if (error?.requiresReauth) {
            navigate('/account/login', {
                state: {
                    from:
                        typeof window !== 'undefined'
                            ? window.location.pathname
                            : '/checkout',
                    message:
                        'Your session has expired. Please sign in to continue.'
                }
            });
        } else if (error?.shouldNotify) {
            dispatch({ type: 'SET_CART_ERROR', error });
        }
    }, []);

    const mountTotals = useCallback(
        async (cart_id, items, shippingInfo, discounts, customerInfo) => {
            let customerInfoToUse = customerInfo;

            if (items && items.length) {
                const {
                    items: cartItems,
                    subTotal,
                    tax,
                    shipping,
                    giftCardBalance,
                    grandTotal,
                    discount,
                    promoCodes
                } = await getTotals(
                    items,
                    customerInfo ? customerInfo.stripe_customer_id : '',
                    discounts
                );
                dispatch({
                    type: 'MOUNT_CART',
                    cart_id,
                    items: normalizeCartItemsForDisplay(cartItems),
                    totals: {
                        subTotal,
                        tax,
                        shipping,
                        giftCardBalance,
                        grandTotal,
                        discount
                    },
                    shippingInfo,
                    discounts: promoCodes,
                    customerInfo: customerInfoToUse
                });
            } else {
                dispatch({
                    type: 'MOUNT_CART',
                    cart_id: '',
                    items: [],
                    discounts: [],
                    totals: {
                        subTotal: 0,
                        tax: 0,
                        shipping: 0,
                        giftCardBalance: 0,
                        grandTotal: 0,
                        discount: 0
                    },
                    shippingInfo: shippingInfo || {},
                    customerInfo: customerInfoToUse
                });
            }
        },
        []
    );

    useEffect(() => {
        const cart = JSON.parse(localStorage.getItem(CART_STORAGE_KEY));
        const { cart_id, items, shippingInfo, discounts, customerInfo } =
            cart || {};

        if (!userLoading && !state.hasMounted) {
            if (cart_id) {
                mountTotals(
                    cart_id,
                    items || [],
                    shippingInfo || {},
                    discounts || [],
                    customerInfo || {}
                );
                cartHeartbeat.start(cart_id);
            } else {
                mountTotals('', [], {}, [], {});
                cartHeartbeat.stop();
            }
        }
    }, [mountTotals, userLoading, state.hasMounted]);

    useEffect(() => {
        const justLoggedIn =
            isLoggedIn && !prevIsLoggedIn.current && state.hasMounted;

        if (justLoggedIn) {
            const reloadCartAfterLogin = async () => {
                const cart = JSON.parse(localStorage.getItem(CART_STORAGE_KEY));

                // Email matches or no email in cart - proceed with cart restoration
                if (cart && cart.items && cart.items.length > 0) {
                    const totalInfo = await getTotals(
                        cart.items,
                        user.stripe_customer_id,
                        cart.discounts || []
                    );

                    dispatch({
                        type: 'MOUNT_CART',
                        cart_id: cart.cart_id,
                        items: normalizeCartItemsForDisplay(totalInfo.items),
                        totals: {
                            subTotal: totalInfo.subTotal,
                            tax: totalInfo.tax,
                            shipping: totalInfo.shipping,
                            giftCardBalance: totalInfo.giftCardBalance,
                            grandTotal: totalInfo.grandTotal,
                            discount: totalInfo.discount
                        },
                        shippingInfo: cart.shippingInfo || {},
                        discounts: totalInfo.promoCodes || cart.discounts || [],
                        customerInfo: {
                            stripe_customer_id: user.stripe_customer_id,
                            name: user.name,
                            email: user.email,
                            phone: user.phone_number
                        }
                    });
                } else {
                    dispatch({
                        type: 'MOUNT_CART',
                        cart_id: '',
                        items: [],
                        discounts: [],
                        totals: {
                            subTotal: 0,
                            tax: 0,
                            shipping: 0,
                            giftCardBalance: 0,
                            grandTotal: 0,
                            discount: 0
                        },
                        shippingInfo: {},
                        customerInfo: {
                            stripe_customer_id: user.stripe_customer_id,
                            name: user.name,
                            email: user.email,
                            phone: user.phone_number
                        }
                    });
                }
            };

            reloadCartAfterLogin();
        } else if (isLoggedIn && state.hasMounted) {
            dispatch({
                type: 'UPDATE_CUSTOMER_INFO',
                customerInfo: {
                    stripe_customer_id: user.stripe_customer_id,
                    name: user.name,
                    email: user.email,
                    phone: user.phone_number
                }
            });
        }

        prevIsLoggedIn.current = isLoggedIn;

        return () => {
            if (state.hasMounted) {
                if (isLoggedIn) {
                    dispatch({ type: 'CLEAR', clearCustomer: true });
                } else {
                    dispatch({ type: 'CLEAR_INVOICES' });
                }
            }
        };
    }, [isLoggedIn, user, state.hasMounted]);

    useEffect(() => {
        if (state.hasMounted) {
            if (state.cart_id) {
                cartHeartbeat.start(state.cart_id);
            } else {
                cartHeartbeat.stop();
            }
        }
    }, [state.cart_id, state.hasMounted]);

    useEffect(() => {
        const { hasMounted, items, customerInfo, discounts } = stateRef.current;

        if (!hasMounted || !selectedLocation || items.length === 0) {
            prevSelectedLocation.current = selectedLocation;
            return;
        }

        const locationChanged =
            prevSelectedLocation.current &&
            prevSelectedLocation.current.locationId !==
                selectedLocation.locationId;

        prevSelectedLocation.current = selectedLocation;

        if (!locationChanged) return;

        const recalculateForNewLocation = async () => {
            dispatch({ type: 'LOADING' });

            let currentItems = items;
            let payload;

            for (const item of currentItems) {
                if (item.bogoItem) continue;
                payload = await updateCartItemLocation(
                    item.cartItemId,
                    selectedLocation,
                    currentItems,
                    customerInfo?.stripe_customer_id,
                    discounts
                );
                if (payload?.error) {
                    handleCartError(payload.error);
                }
                currentItems = payload?.items || currentItems;
            }

            if (payload) {
                dispatch({ type: 'UPDATE_ITEMS', ...payload });
            }
        };

        recalculateForNewLocation();
    }, [selectedLocation, handleCartError]);

    return (
        <CartContext.Provider
            value={{
                ...state,
                addToCart: async (
                    serviceId,
                    displayName,
                    location,
                    addOnIds,
                    giftCardValue,
                    vehicleID,
                    garageID,
                    quantity,
                    giftCardQuantity,
                    addPromo = false,
                    slug = null,
                    isPhysicalGiftCard = false
                ) => {
                    dispatch({ type: 'LOADING' });
                    const payload = await addItemToCart(
                        serviceId,
                        location,
                        state.items,
                        state.customerInfo?.stripe_customer_id,
                        state.discounts,
                        displayName,
                        addOnIds,
                        giftCardValue,
                        vehicleID,
                        garageID,
                        quantity,
                        giftCardQuantity,
                        addPromo,
                        slug,
                        isPhysicalGiftCard
                    );

                    // Check if there's an error to display
                    if (payload?.error) {
                        handleCartError(payload.error);
                    }

                    dispatch({ type: 'UPDATE_ITEMS', ...payload });
                    dispatch({ type: 'TOGGLE_ITEMS_UPDATED' });
                },
                toggleItemsUpdated: () => {
                    dispatch({ type: 'TOGGLE_ITEMS_UPDATED' });
                },
                addCartPromoCode: async (promoCode) => {
                    dispatch({ type: 'LOADING' });
                    const payload = await updateCartPromoCode(
                        promoCode,
                        state.discounts,
                        state.items,
                        state.customerInfo?.stripe_customer_id
                    );

                    if (payload.error) {
                        handleCartError(payload.error);
                    }

                    if (payload.success) {
                        dispatch({ type: 'UPDATE_ITEMS', ...payload });
                        return { success: true };
                    } else {
                        dispatch({ type: 'UPDATE_ITEMS', ...payload });
                        return {
                            success: false,
                            promoCodeErrorMsg: payload.promoCodeErrorMsg
                        };
                    }
                },
                removePromoCode: async (promoCode) => {
                    dispatch({ type: 'LOADING' });
                    const payload = await removePromoCode(
                        promoCode,
                        state.items,
                        state.customerInfo?.stripe_customer_id,
                        state.discounts
                    );

                    if (payload.error) {
                        handleCartError(payload.error);
                    }

                    dispatch({ type: 'UPDATE_ITEMS', ...payload });
                },
                updateCartItemAddOns: async (cartItemId, addOns, location) => {
                    dispatch({ type: 'LOADING' });
                    const payload = await updateCartItemAddOns(
                        cartItemId,
                        addOns,
                        state.items,
                        location,
                        state.customerInfo?.stripe_customer_id,
                        state.discounts
                    );

                    if (payload.error) {
                        handleCartError(payload.error);
                    }

                    dispatch({ type: 'UPDATE_ITEMS', ...payload });
                },
                updateAddOnPaymentMethod: async (
                    cartItemId,
                    addOnId,
                    useBells
                ) => {
                    dispatch({ type: 'LOADING' });
                    const payload = await updateAddOnPaymentMethod(
                        cartItemId,
                        addOnId,
                        useBells,
                        state.items,
                        state.customerInfo?.stripe_customer_id,
                        state.discounts
                    );

                    if (payload.error) {
                        handleCartError(payload.error);
                    }

                    dispatch({ type: 'UPDATE_ITEMS', ...payload });
                },
                removeFromCart: async (cartItemId) => {
                    dispatch({ type: 'LOADING' });
                    const payload = await removeItemFromCart(
                        cartItemId,
                        state.items,
                        state.customerInfo?.stripe_customer_id,
                        state.discounts
                    );

                    if (payload.error) {
                        handleCartError(payload.error);
                    }

                    dispatch({ type: 'UPDATE_ITEMS', ...payload });
                },
                removeAddOnFromCart: async (cartItemId, addOnId) => {
                    dispatch({ type: 'LOADING' });
                    const payload = await removeCartItemAddOn(
                        cartItemId,
                        addOnId,
                        state.items,
                        state.customerInfo?.stripe_customer_id,
                        state.discounts
                    );

                    if (payload.error) {
                        handleCartError(payload.error);
                    }

                    dispatch({ type: 'UPDATE_ITEMS', ...payload });
                },
                setGiftCardInformation: (giftCards) => {
                    dispatch({ type: 'UPDATE_GIFT_CARD_INFO', giftCards });
                },
                setCartVehicles: async (vehicles) => {
                    dispatch({ type: 'LOADING' });

                    let items = [...state.items];

                    forEach(keys(vehicles), (cartItemId) => {
                        const index = findIndex(items, { cartItemId });
                        const vehicle = vehicles[cartItemId];
                        if (vehicle.vehicleId === '') {
                            vehicle.vehicleId = null;
                        }
                        items[index].vehicle = vehicle;
                    });

                    const stripe_customer_id = user?.stripe_customer_id;

                    updateRecipientsIfLoggedIn(items, user);

                    const totalInfo = await getTotals(
                        items,
                        stripe_customer_id,
                        state.discounts
                    );

                    await syncCartToStorage(totalInfo.items, state.discounts, {
                        subTotal: totalInfo.subTotal,
                        tax: totalInfo.tax,
                        shipping: totalInfo.shipping,
                        grandTotal: totalInfo.grandTotal,
                        discount: totalInfo.discount
                    });

                    dispatch({ type: 'UPDATE_VEHICLES', ...totalInfo });
                },
                setCustomerInformation: async (customerInfo) => {
                    dispatch({ type: 'UPDATE_CUSTOMER_INFO', customerInfo });
                    await syncCartToStorage(
                        state.items,
                        state.discounts,
                        null,
                        null,
                        customerInfo
                    );
                },
                clearCart: (clearCustomer) => {
                    dispatch({ type: 'CLEAR', clearCustomer });
                    cartHeartbeat.stop();
                },
                recalculateTotals: async () => {
                    dispatch({ type: 'LOADING' });
                    const payload = await getTotals(
                        state.items,
                        state.customerInfo?.stripe_customer_id,
                        state.discounts
                    );

                    const cartError = await syncCartToStorage(
                        payload.items,
                        state.discounts,
                        {
                            subTotal: payload.subTotal,
                            tax: payload.tax,
                            shipping: payload.shipping,
                            grandTotal: payload.grandTotal,
                            discount: payload.discount
                        }
                    );

                    if (cartError) {
                        handleCartError(cartError);
                    }

                    dispatch({ type: 'UPDATE_ITEMS', ...payload });
                },
                setPhysicalProductInfo: async (shippingInfo) => {
                    dispatch({
                        type: 'UPDATE_PHYSICAL_PRODUCT_INFO',
                        shippingInfo
                    });

                    const cart = JSON.parse(
                        localStorage.getItem(CART_STORAGE_KEY) || '{}'
                    );
                    cart.shippingInfo = shippingInfo;
                    localStorage.setItem(
                        CART_STORAGE_KEY,
                        JSON.stringify(cart)
                    );

                    await syncCartToStorage(state.items, state.discounts);
                },
                updateCartItemQuantity: async (
                    cartItemId,
                    quantity,
                    location
                ) => {
                    dispatch({ type: 'LOADING' });
                    const payload = await updateCartItemQuantity(
                        cartItemId,
                        quantity,
                        state.items,
                        location,
                        state.customerInfo?.stripe_customer_id,
                        state.discounts
                    );

                    if (payload.error) {
                        handleCartError(payload.error);
                    }

                    dispatch({ type: 'UPDATE_ITEMS', ...payload });
                },
                updateCartItemPrice: async (cartItemId, newPrice) => {
                    dispatch({ type: 'LOADING' });
                    const payload = await updateCartItemPrice(
                        cartItemId,
                        newPrice,
                        state.items,
                        state.customerInfo?.stripe_customer_id,
                        state.discounts
                    );

                    if (payload.error) {
                        handleCartError(payload.error);
                    }

                    dispatch({ type: 'UPDATE_ITEMS', ...payload });
                },
                updateCartItemsWithVehicleInfo: (newCartItems) => {
                    const newItems = map(state.items, (item) => {
                        const newItem = find(newCartItems, {
                            cartItemId: item.cartItemId
                        });

                        if (isEmpty(newItem)) {
                            return item;
                        }

                        return {
                            ...item,
                            ...newItem
                        };
                    });

                    dispatch({
                        type: 'UPDATE_CART_WITH_VEHICLE_INFO',
                        newItems
                    });
                },
                updateCartItemLocation: async (cartItemId, locationID) => {
                    dispatch({ type: 'LOADING' });
                    const location = find(locations, {
                        locationId: locationID
                    });
                    const payload = await updateCartItemLocation(
                        cartItemId,
                        location,
                        state.items,
                        state.customerInfo?.stripe_customer_id,
                        state.discounts
                    );

                    if (payload.error) {
                        handleCartError(payload.error);
                    }

                    dispatch({ type: 'UPDATE_ITEMS', ...payload });
                },
                clearCartError: () => {
                    dispatch({ type: 'CLEAR_CART_ERROR' });
                },
                reloadCartFromStorage: useCallback(async () => {
                    const cart = JSON.parse(
                        localStorage.getItem(CART_STORAGE_KEY)
                    );
                    if (cart && cart.cart_id) {
                        await mountTotals(
                            cart.cart_id,
                            cart.items || [],
                            cart.shippingInfo || {},
                            cart.discounts || [],
                            cart.customerInfo || {}
                        );
                        cartHeartbeat.start(cart.cart_id);
                    }
                }, [mountTotals])
            }}
        >
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);
